import { Box, Heading, HStack, VStack, Text } from "@chakra-ui/react";
import { CloseNewSVG } from "assets";
import { AbstractModal, CopyButton, ExchangeInfoDetail, ExchangeInfoDetailProps } from "components";
import { parseISO } from "date-fns";
import { format } from "date-fns/esm";
import { useCopy } from "hooks";
import { ISingleCicoTransferRo } from "interfaces";
import { readableStatus, showFirstLastChar, transferStatusColor } from "utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: ISingleCicoTransferRo;
}

const CicoTransferDetailsModal = ({ isOpen, onClose, data }: Props) => {
  return (
    <>
      <AbstractModal
        isOpen={isOpen}
        onClose={onClose}
        isCentered={false}
        _content={{
          maxW: { base: "350px", md: "425px" },
          borderRadius: "20px",
          p: { base: "53px 14px 32px", md: "24px 24px" },
          bg: "#fff",
        }}
      >
        <Box
          pos="absolute"
          top={{ base: "3%", md: "5%" }}
          right={{ base: "6%", md: "4%" }}
          onClick={onClose}
          cursor="pointer"
          _hover={{
            zoom: "1.05",
            transition: "all .5s ease-in",
          }}
        >
          <CloseNewSVG />
        </Box>

        <Heading p="0" fontSize="24px" textAlign="center" mb="24px">
          Transfer Details
        </Heading>

        <VStack spacing="20px" width="100%">
          <ExchangeInfoDetail
            width="100%"
            title="Fiat Amount"
            description={`${data?.originalFiatAmount.toLocaleString()} ${data.fiatType}`}
          />

          <ExchangeInfoDetail width="100%" title="Crypto Amount" description={`${data?.cryptoVolume} ${data.cryptoType}`} />

          <ExchangeInfoDetail width="100%" title="Fee" description={`${data?.fee} ${data.feeUnit}`} />

          <ExchangeInfoDetail width="100%" title="Account Name" description={`${data?.fiatAccountData.accountName}`} />

          <ExchangeInfoDetail width="100%" canCopy title="Account Number" description={`${data?.fiatAccountData.accountNumber}`} />

          <ExchangeInfoDetail
            width="100%"
            title="Bank Code"
            description={`${String(data?.fiatAccountData.networkOrBankCode).toUpperCase()}`}
          />

          {data?.txHash && (
            <TxHash
              width="100%"
              canCopy
              title="Hash"
              copyValue={data.txHash}
              description={`${showFirstLastChar(data.txHash, 6)}`}
            />
          )}

          {data?.reference && <ExchangeInfoDetail width="100%" canCopy title="Reference" description={`${data.reference}`} />}

          <ExchangeInfoDetail
            width="100%"
            title="Transfer Status"
            descriptionColor={transferStatusColor(data.transferStatus)!}
            description={readableStatus(data?.transferStatus)}
          />

          {data.transferStatus === "TransferWaitingForUserAction" && (
            <Box
              mt="-4px !important"
              fontSize="14px"
              width="100%"
              color="#3ac1a1"
              fontWeight="600"
              textDecor="underline"
              textAlign="right"
            >
              <a
                href={`https://bitmama-widget.web.app/pay?for=cico&class=1&txId=${data._id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                complete payment
              </a>
            </Box>
          )}

          <ExchangeInfoDetail
            width="100%"
            title="Date/Time"
            description={`${format(parseISO(data.createdAt), "dd-MM-yyyy, hh:mm a")}`}
          />
        </VStack>
      </AbstractModal>
    </>
  );
};

export default CicoTransferDetailsModal;

// TEMPORARY FIX
export function TxHash(props: ExchangeInfoDetailProps) {
  const { title, description, copyValue, canCopy = false, _title, _description, descriptionColor, ...xprops } = props;

  const { onCopy } = useCopy(copyValue ?? "");
  return (
    <HStack justifyContent="space-between" fontSize="14px" fontWeight="500" {...xprops}>
      <Text {..._title}>{title}</Text>
      <HStack>
        <Text color={descriptionColor ? descriptionColor : ""} {..._description}>
          {description}
        </Text>
        {!!canCopy && <CopyButton onClick={onCopy} />}
      </HStack>
    </HStack>
  );
}
